import { useAtom } from '@reatom/npm-react'
import * as DateF from 'date-fns'
import { useMemo } from 'react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { CurrencyIcon } from '@root/entities/currency/ui'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { reatomCommentsHistory } from '@root/features/comment/atoms'
import { useFormatDuration } from '@root/shared'
import { useTranslation } from '@root/shared/localization'

import type { Comment } from '@root/entities/comment'
import type { CurrencyModel } from '@root/entities/currency/model'
import type { DelugeId } from '@root/entities/user-short'

/**
 * What this person has been saying, beside who they are.
 *
 * The column used to hold their open positions, which on most profiles is an
 * empty table under a heading — a card that only ever says "No Data Found".
 * What a profile is actually read for is the person, so it holds their posts:
 * the token each one is about, what they said, and when.
 *
 * Their own latest, not everybody's: this column belongs to the profile it is
 * on, and a global feed here would say nothing about whose page you are
 * looking at.
 */
type Props = {
  delugeId: DelugeId
}

/** Enough to show what somebody is about without becoming the page. */
const SHOWN = 8

export const LatestPostsBoard: React.FC<Props> = ({ delugeId }) => {
  const { t } = useTranslation()
  const [network] = useAtom(selectedNetworkAtom)

  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomCommentsHistory({
        delugeId,
        type: 'comments',
        period: 'ALL',
        network: Network.toDto(network),
      }),
    [delugeId, network],
  )

  const [state] = useAtom(model.loadCommentsRequestState)
  const [history] = useAtom(model.historyAtom)

  const shown = history.slice(0, SHOWN)

  return (
    <Root>
      <Header>
        <Title>{t('comment.comments')}</Title>
      </Header>

      <Body>
        {AsyncStateN.matchWaiting(state, {
          Waiting: () => <Muted>{t('common.loading')}</Muted>,
          // The column is one card among several; a failed read says so
          // quietly rather than taking the page over with an error panel.
          Error: () => <Muted>{t('common.something-went-wrong')}</Muted>,
          Success: () =>
            shown.length === 0 ? (
              <Muted>{t('comment.history.empty-description')}</Muted>
            ) : (
              <List>
                {shown.map((entry) =>
                  entry.comments.map((comment) => (
                    <PostRow
                      key={comment.id}
                      comment={comment}
                      currency={entry.pair.quote}
                    />
                  )),
                )}
              </List>
            ),
        })}
      </Body>
    </Root>
  )
}

const PostRow: React.FC<{
  comment: Comment.Comment
  currency: CurrencyModel
}> = ({ comment, currency }) => {
  const { t } = useTranslation()
  const { formatDuration } = useFormatDuration()

  const when = useMemo(() => {
    const createdAt = DateF.parseISO(comment.createdAt)
    if (DateF.differenceInSeconds(createdAt, new Date()) >= 0) {
      return t('common.now')
    }
    return formatDuration({
      startAt: createdAt,
      mode: 'singleUnit',
      withAgo: true,
    })
  }, [comment.createdAt, formatDuration, t])

  return (
    <Row>
      <RowHead>
        <CurrencyIcon currency={currency} size={16} withPreview={false} />
        <Ticker>{currency.ticker}</Ticker>
        <When>{when}</When>
      </RowHead>
      <Content>{comment.content}</Content>
    </Row>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);

  overflow: hidden;
  border-radius: 12px;
  border: 1px solid var(--background-3);
  background: var(--appearance-1);
`

const Header = styled.div`
  padding: 12px 14px;
  border-bottom: 1px solid var(--background-3);
  align-items: center;
  display: flex;
  justify-content: center;
  gap: 10px;
  text-align: center;
`

const Title = styled.div`
  color: var(--text-11);
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
`

const Body = styled.div`
  min-height: 0;
  overflow-y: auto;
`

const Muted = styled.div`
  padding: 24px 14px;
  text-align: center;
  color: var(--text-9);
  font-size: 12px;
`

const List = styled.div`
  display: flex;
  flex-direction: column;
`

const Row = styled.div`
  display: grid;
  gap: 4px;
  padding: 10px 14px;

  & + & {
    border-top: 1px solid var(--background-3);
  }
`

const RowHead = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const Ticker = styled.span`
  color: var(--text-12);
  font-size: 12px;
  font-weight: 600;
`

const When = styled.span`
  margin-left: auto;
  color: var(--text-9);
  font-size: 11px;
`

const Content = styled.div`
  color: var(--text-11);
  font-size: 12px;
  line-height: 16px;

  /* Two lines and an ellipsis: the column is a glance, not the thread. */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  overflow-wrap: anywhere;
`
